import { useEffect, useState, useRef } from "react";
import { post } from "../js/post";
import {
  focusElement,
  formatValueUnit,
  recordTypeOptionList,
  userData,
} from "../js/utils";
import moment from "moment";
import "moment/locale/es";
import {
  salesStatsAgentsTable,
  salesStatsOpticsTable,
} from "../js/tableColumns";
import DataTable from "react-data-table-component";
import Dropdown from "./Dropdown";
import CheckBox from "./CheckBox";
import ButtonIconH from "./ButtonIconH";
import { TabSelector } from "./TabSelector";
import InputIconSearch from "./InputIconSearch";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  BarChart,
  Rectangle,
} from "recharts";
import {
  axisStyle,
  axisXLabel,
  axisYLabel,
  barChartMargin,
  barColors,
  getHistoricalBarLabel,
  getPMVBarLabel,
  getProfitBarLabel,
  legendStyle,
  titleStyle,
  xAxisTick,
} from "../js/barChartStyle";
import { useGlassesTypes } from "../hooks/useGlassesTypes";

export function DashboardSalesPanelStatsView({ openCashPopup }) {
  focusElement(6);
  const [opticsRankingList, setOpticsRankingList] = useState([]);
  const [opticsRankingListFiltered, setOpticsRankingListFiltered] = useState(
    [],
  );
  const [agentsRankingList, setAgentsRankingList] = useState([]);
  const [agentsRankingListFiltered, setAgentsRankingListFiltered] = useState(
    [],
  );
  const [selectedOptic, setSelectedOptic] = useState({ id: "", name: "" });
  const [selectedAgent, setSelectedAgent] = useState({ id: "", name: "" });
  const [opticsSearchYear, setOpticsSearchYear] = useState(moment());
  const [agentsSearchYear, setAgentsSearchYear] = useState(moment());
  const [selectedTimeView, setSelectedTimeView] = useState("Histórico");
  const [selectedYearView, setSelectedYearView] = useState("5 Años");
  const [historicalStatsDashboardList, setHistoricalStatsDashboardList] =
    useState([]);
  const [profitStatsDashboardList, setProfitStatsDashboardList] = useState([]);
  const [PMVStatsDashboardList, setPMVStatsDashboardList] = useState([]);
  const [PMVRecordType, setPMVRecordType] = useState({
    id: "Vista",
    text: "VISTA",
  });
  const [PMVSearchYear, setPMVSearchYear] = useState(moment());
  const [PMVSelectedTab, setPMVSelectedTab] = useState("Tipo ficha");
  const [PMVCustomRecordTypeList, setPMVCustomRecordTypeList] = useState([]);
  const PMVCustomGlassesTypeList = useGlassesTypes();
  const [PMVCustomRecordType, setPMVCustomRecordType] = useState({
    id: "",
    text: "",
  });
  const [PMVCustomGlassesType, setPMVCustomGlassesType] = useState({
    id: "",
    text: "",
  });
  const [PMVIncludeRange, setPMVIncludeRange] = useState(false);
  const [opticalSearch, setOpticalSearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");

  const chartContainerRef = useRef(null);

  const conditionalRowStylesOpticTable = [
    {
      when: (row) => row.idOptica === selectedOptic.id,
      classNames: ["selected-optic-row"],
    },
  ];

  const conditionalRowStylesAgentTable = [
    {
      when: (row) => row.idAgente === selectedAgent.id,
      classNames: ["selected-optic-row"],
    },
  ];

  async function getOpticsRankingStatsDashboard() {
    const response = await post(
      "/support-extra/dashboard/ObtenerEstadisticasRankingOpticasDashboard.php",
      {
        token: userData.token,
        ano: moment(opticsSearchYear).year(),
      },
    );
    if (response.length > 0) {
      setSelectedOptic({
        id: response[0].idOptica,
        name: response[0].nombreOptica,
      });
    }
    setOpticsRankingList(response);
    setOpticsRankingListFiltered(response);
  }

  async function getAgentsRankingStatsDashboard() {
    const response = await post(
      "/support-extra/dashboard/ObtenerEstadisticasRankingAgentesDashboard.php",
      {
        token: userData.token,
        ano: moment(agentsSearchYear).year(),
      },
    );
    setAgentsRankingList(response);
    setAgentsRankingListFiltered(response);
  }

  async function getHistoricalSalesStatsDashboard() {
    const response = await post(
      "/support-extra/dashboard/ObtenerEstadisticasVentaHistoricoDashboard.php",
      {
        token: userData.token,
        idOptica: selectedOptic.id,
        idAgente: selectedAgent.id,
        ultimosAnos: selectedYearView === "5 Años" ? 5 : 10,
      },
    );
    setHistoricalStatsDashboardList(response);
  }

  const getRecordTypeList = async () => {
    let response = await post("/support-extra/fichas/ListarTipoFichas.php", {
      token: userData.token,
      familia:
        PMVRecordType.id === "Vista"
          ? 4
          : PMVRecordType.id === "Sol" || PMVRecordType.id === "VistaSol"
            ? 6
            : "",
    });
    setPMVCustomRecordTypeList([
      { id: "", text: "" },
      ...response.map((e) => ({ id: e.codigo, text: e.descripcion })),
    ]);
  };

  async function getPMVSalesStatsDashboard() {
    const response = await post(
      "/support-extra/dashboard/ObtenerEstadisticasVentaPMVDashboard.php",
      {
        token: userData.token,
        idOptica: selectedOptic.id,
        idAgente: selectedAgent.id,
        ficha: PMVRecordType.id,
        ano: moment(PMVSearchYear).year(),
        tipoGrafica: PMVSelectedTab.replace(/\s/g, ""),
        personalizadaTipoFicha: PMVCustomRecordType.id,
        personalizadaTipoGafa: PMVCustomGlassesType.id,
        personalizadaIncluirGama:
          PMVCustomGlassesType.id === "PV" ? PMVIncludeRange : false,
      },
    );
    setPMVStatsDashboardList(response);
  }

  async function getDashboardSalesProfitStats() {
    const response = await post(
      "/support-extra/dashboard/ObtenerEstadisticasVentaBeneficiosDashboard.php",
      {
        token: userData.token,
        idOptica: selectedOptic.id,
        idAgente: selectedAgent.id,
        ultimosAnos: selectedYearView === "5 Años" ? 5 : 10,
      },
    );
    setProfitStatsDashboardList(response);
  }

  function checkFrameType() {
    return (
      PMVRecordType.id === "Vista" ||
      PMVRecordType.id === "Sol" ||
      PMVRecordType.id === "VistaSol"
    );
  }

  const updatedColumnsOp = salesStatsOpticsTable.map((col) => {
    if (col.name === "") {
      return {
        ...col,
        cell: (row) =>
          row.idOptica ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openCashPopup(
                  row.idOptica,
                  "",
                  true,
                  moment(
                    `${moment(opticsSearchYear).format("YYYY")}-01-01`,
                    "YYYY-MM-DD",
                  ).format("YYYY-MM-DD"),
                  moment(
                    `${moment(opticsSearchYear).format("YYYY")}-12-31`,
                    "YYYY-MM-DD",
                  ).format("YYYY-MM-DD"),
                  0,
                  row.nombreOptica,
                  "",
                );
                setSelectedOptic({
                  id: row.idOptica,
                  name: row.nombreOptica,
                });
                setSelectedAgent({ id: "", name: "" });
              }}
              tabIndex={-1}
            >
              Ver más
            </button>
          ) : (
            ""
          ),
      };
    }

    return col;
  });

  const updatedColumnsAg = salesStatsAgentsTable.map((col) => {
    if (col.name === "") {
      return {
        ...col,
        cell: (row) =>
          row.idAgente ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openCashPopup(
                  "",
                  "",
                  true,
                  moment(
                    `${moment(opticsSearchYear).format("YYYY")}-01-01`,
                    "YYYY-MM-DD",
                  ).format("YYYY-MM-DD"),
                  moment(
                    `${moment(opticsSearchYear).format("YYYY")}-12-31`,
                    "YYYY-MM-DD",
                  ).format("YYYY-MM-DD"),
                  0,
                  row.nombreApellidosAgente,
                  row.idAgente,
                );
                setSelectedAgent({
                  id: row.idAgente,
                  name: row.nombreApellidosAgente,
                });
                setSelectedOptic({ id: "", name: "" });
              }}
              tabIndex={-1}
            >
              Ver más
            </button>
          ) : (
            ""
          ),
      };
    }

    return col;
  });

  useEffect(() => {
    getOpticsRankingStatsDashboard();
  }, [opticsSearchYear]);

  useEffect(() => {
    getAgentsRankingStatsDashboard();
  }, [agentsSearchYear]);

  useEffect(() => {
    getRecordTypeList();
  }, []);

  useEffect(() => {
    const opticSelected = selectedOptic?.id !== "";
    const agentSelected = selectedAgent?.id !== "";

    if (!opticSelected && !agentSelected) return;

    switch (selectedTimeView) {
      case "Histórico":
        getHistoricalSalesStatsDashboard();
        break;
      case "PMV":
        getPMVSalesStatsDashboard();
        break;
      case "Beneficios":
        getDashboardSalesProfitStats();
        break;
      default:
        break;
    }
  }, [
    selectedTimeView,
    selectedOptic,
    selectedAgent,
    selectedYearView,
    PMVSelectedTab,
    PMVRecordType,
    PMVSearchYear,
  ]);

  useEffect(() => {
    if (PMVRecordType.id === "LC" || PMVRecordType.id === "Audifonos") {
      setPMVSelectedTab("Tipo ficha");
    }
  }, [PMVRecordType]);

  useEffect(() => {
    if (opticalSearch !== "") {
      setOpticsRankingListFiltered(
        opticsRankingList.filter((e) => e.nombreOptica.includes(opticalSearch)),
      );
    } else setOpticsRankingListFiltered(opticsRankingList);
  }, [opticalSearch]);

  useEffect(() => {
    if (agentSearch !== "") {
      setAgentsRankingListFiltered(
        agentsRankingList.filter((e) =>
          e.nombreApellidosAgente.includes(agentSearch),
        ),
      );
    } else setAgentsRankingListFiltered(agentsRankingList);
  }, [agentSearch]);

  return (
    <div className="dashboard-sales-filter">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 40,
            padding: "20px 0",
          }}
        >
          <div className="dashboard-summary-table" style={{ width: "50%" }}>
            <div className="opt-table-title opt-table-title-dash-stats">
              Ranking ópticas {moment(opticsSearchYear).year()}
              <div className="data-field">
                <button
                  className="incdec-btn"
                  tabIndex={-1}
                  onClick={() => {
                    setOpticsSearchYear((prev) =>
                      moment(prev).subtract(1, "year"),
                    );
                  }}
                >
                  {"<"}
                </button>
                <span style={{ width: "60px", textAlign: "center" }}>
                  {moment(opticsSearchYear).year()}
                </span>
                <button
                  className="incdec-btn"
                  tabIndex={-1}
                  onClick={() => {
                    setOpticsSearchYear((prev) => moment(prev).add(1, "year"));
                  }}
                >
                  {">"}
                </button>
              </div>
            </div>
            <div className="data-field">
              <InputIconSearch
                size={3}
                tabIndex={6}
                placeholder={"Óptica"}
                input={opticalSearch}
                onChange={async (e) => {
                  setOpticalSearch(e);
                }}
                onClear={async () => {
                  setOpticalSearch("");
                }}
              ></InputIconSearch>
            </div>
            <DataTable
              columns={updatedColumnsOp}
              data={opticsRankingListFiltered}
              className="custom-datatable dashboard-custom-table"
              noDataComponent="No hay datos disponibles."
              conditionalRowStyles={conditionalRowStylesOpticTable}
              onRowClicked={(row) => {
                setSelectedOptic({
                  id: row.idOptica,
                  name: row.nombreOptica,
                });
                setSelectedAgent({ id: "", name: "" });
              }}
            />
            {opticsRankingListFiltered.length > 0 && (
              <DataTable
                columns={updatedColumnsOp}
                data={[
                  {
                    idOptica: "",
                    nombreOptica: "",
                    totalVD: opticsRankingListFiltered.reduce(
                      (acc, e) => acc + e.totalVD,
                      0,
                    ),
                    beneficio: "",
                  },
                ]}
                className="custom-datatable summary-row no-header-datatable"
              />
            )}
          </div>
          <div className="dashboard-summary-table" style={{ width: "50%" }}>
            <div className="opt-table-title  opt-table-title-dash-stats">
              Ranking agentes {moment(agentsSearchYear).year()}
              <div className="data-field">
                <button
                  className="incdec-btn"
                  tabIndex={-1}
                  onClick={() => {
                    setAgentsSearchYear((prev) =>
                      moment(prev).subtract(1, "year"),
                    );
                  }}
                >
                  {"<"}
                </button>
                <span style={{ width: "60px", textAlign: "center" }}>
                  {moment(agentsSearchYear).year()}
                </span>
                <button
                  className="incdec-btn"
                  tabIndex={-1}
                  onClick={() => {
                    setAgentsSearchYear((prev) => moment(prev).add(1, "year"));
                  }}
                >
                  {">"}
                </button>
              </div>
            </div>
            <div className="data-field">
              <InputIconSearch
                size={3}
                tabIndex={7}
                placeholder={"Agente"}
                input={agentSearch}
                onChange={async (e) => {
                  setAgentSearch(e);
                }}
                onClear={async () => {
                  setAgentSearch("");
                }}
              ></InputIconSearch>
            </div>
            <DataTable
              columns={updatedColumnsAg}
              data={agentsRankingListFiltered}
              className="custom-datatable dashboard-custom-table"
              noDataComponent="No hay datos disponibles."
              conditionalRowStyles={conditionalRowStylesAgentTable}
              onRowClicked={(row) => {
                setSelectedAgent({
                  id: row.idAgente,
                  name: row.nombreApellidosAgente,
                });
                setSelectedOptic({ id: "", name: "" });
              }}
            />
            {agentsRankingListFiltered.length > 0 && (
              <DataTable
                columns={updatedColumnsAg}
                data={[
                  {
                    idAgente: "",
                    nombreApellidosAgente: "",
                    totalVD: agentsRankingListFiltered.reduce(
                      (acc, e) => acc + e.totalVD,
                      0,
                    ),
                    beneficio: "",
                  },
                ]}
                className="custom-datatable summary-row no-header-datatable"
              />
            )}
          </div>
        </div>
        {selectedOptic.id !== "" && (
          <span className="dashboard-section-or-title">
            {selectedOptic.id + " - " + selectedOptic.name}
          </span>
        )}
        {selectedAgent.id !== "" && (
          <span className="dashboard-section-or-title">
            {selectedAgent.id + " - " + selectedAgent.name}
          </span>
        )}
        <div>
          <TabSelector
            options={["Histórico", "PMV", "Beneficios"]}
            selected={selectedTimeView}
            style={{ borderBottom: "none" }}
            onChange={(e) => {
              setSelectedTimeView(e);
              if (e !== "PMV") {
                setPMVSelectedTab("Tipo ficha");
              }
            }}
          />
          <div className="thin-section-rounded-border">
            {selectedTimeView === "Histórico" && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                <TabSelector
                  options={["5 Años", "10 Años"]}
                  selected={selectedYearView}
                  onChange={(e) => {
                    setSelectedYearView(e);
                  }}
                />
                <div style={{ width: "100%", height: "550px" }}>
                  <div style={titleStyle}>
                    Ventas y devoluciones anuales (con % de crecimiento)
                  </div>
                  <ResponsiveContainer
                    width="100%"
                    height={500}
                    style={{
                      maxWidth: 1000,
                      margin: "0 auto",
                    }}
                  >
                    <BarChart
                      width={500}
                      height={300}
                      data={historicalStatsDashboardList}
                      margin={barChartMargin}
                      barCategoryGap={5}
                      barGap={0}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="ano"
                        tick={axisStyle}
                        label={axisXLabel("Año")}
                      />
                      <YAxis
                        tick={axisStyle}
                        label={axisYLabel("Número de operaciones")}
                      />
                      <Tooltip />
                      <Legend
                        layout="horizontal"
                        align="left"
                        verticalAlign="top"
                        wrapperStyle={legendStyle}
                      />
                      <Bar
                        dataKey="ventas"
                        name="Ventas"
                        fill={barColors.blue}
                        activeBar={
                          <Rectangle
                            fill={barColors.blue}
                            stroke={barColors.gray}
                          />
                        }
                        label={getHistoricalBarLabel(
                          historicalStatsDashboardList,
                          "ventas",
                          "porcVentas",
                        )}
                      />
                      <Bar
                        dataKey="devoluciones"
                        name="Devoluciones"
                        fill={barColors.orange}
                        activeBar={
                          <Rectangle
                            fill={barColors.orange}
                            stroke={barColors.gray}
                          />
                        }
                        label={getHistoricalBarLabel(
                          historicalStatsDashboardList,
                          "devoluciones",
                          "porcDevoluciones",
                          barColors.orange,
                        )}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {selectedTimeView === "PMV" && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 20,
                  }}
                >
                  <div className="data-field">
                    <span className="label">Ficha</span>
                    <Dropdown
                      tabIndex={65}
                      optionList={recordTypeOptionList}
                      size={1}
                      op={PMVRecordType.text}
                      mandatory={false}
                      onChange={(e) => {
                        setPMVRecordType({
                          id: e.id,
                          text: e.text,
                        });
                      }}
                    ></Dropdown>
                  </div>
                  <div className="data-field">
                    <button
                      className="incdec-btn"
                      tabIndex={-1}
                      onClick={() => {
                        setPMVSearchYear((prev) =>
                          moment(prev).subtract(1, "year"),
                        );
                      }}
                    >
                      {"<"}
                    </button>
                    <span style={{ width: "60px", textAlign: "center" }}>
                      {moment(PMVSearchYear).year()}
                    </span>
                    <button
                      className="incdec-btn"
                      tabIndex={-1}
                      onClick={() => {
                        setPMVSearchYear((prev) => moment(prev).add(1, "year"));
                      }}
                    >
                      {">"}
                    </button>
                  </div>
                </div>
                {checkFrameType() && (
                  <TabSelector
                    options={["Tipo ficha", "Tipo gafa", "Personalizada"]}
                    selected={PMVSelectedTab}
                    onChange={(e) => {
                      setPMVSelectedTab(e);
                    }}
                  />
                )}
                {PMVSelectedTab === "Personalizada" &&
                  PMVRecordType.id !== "LC" &&
                  PMVRecordType.id !== "Audifonos" && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 20,
                      }}
                    >
                      <div className="data-field">
                        <span className="label">Tipo ficha</span>
                        <Dropdown
                          tabIndex={65}
                          optionList={PMVCustomRecordTypeList}
                          size={3}
                          op={PMVCustomRecordType.text}
                          mandatory={false}
                          onChange={(e) => {
                            setPMVCustomRecordType({
                              id: e.id,
                              text: e.text,
                            });
                          }}
                        ></Dropdown>
                      </div>
                      <div className="data-field">
                        <span className="label">Tipo gafa</span>
                        <Dropdown
                          tabIndex={65}
                          optionList={[
                            { id: "", text: "" },
                            ...PMVCustomGlassesTypeList,
                          ]}
                          size={3}
                          op={PMVCustomGlassesType.text}
                          mandatory={false}
                          onChange={(e) => {
                            setPMVCustomGlassesType({
                              id: e.id,
                              text: e.text,
                            });
                            if (e.id !== "PV") setPMVIncludeRange(false);
                          }}
                        ></Dropdown>
                      </div>
                      {PMVCustomGlassesType.id === "PV" && (
                        <div className="data-field">
                          <span className="label">Incluir gama</span>
                          <CheckBox
                            tabIndex={54}
                            active={PMVIncludeRange}
                            onClick={() => {
                              setPMVIncludeRange((prev) => !prev);
                            }}
                          ></CheckBox>
                        </div>
                      )}
                      <ButtonIconH
                        text="Generar gráfico"
                        small={true}
                        onClick={getPMVSalesStatsDashboard}
                      ></ButtonIconH>
                    </div>
                  )}
                {PMVSelectedTab !== "Personalizada" && (
                  <div
                    ref={chartContainerRef}
                    style={{
                      width: "100%",
                      height: "550px",
                    }}
                  >
                    <div style={titleStyle}>
                      Ventas y PMV por Tipo de{" "}
                      {PMVSelectedTab === "Tipo ficha" ? "Ficha" : "Gafa"}
                    </div>
                    <ResponsiveContainer
                      width="100%"
                      height={500}
                      style={{
                        maxWidth: 1000,
                        margin: "0 auto",
                      }}
                    >
                      <BarChart
                        width={500}
                        height={300}
                        data={PMVStatsDashboardList}
                        barCategoryGap={5}
                        barGap={0}
                        margin={{
                          top: 20,
                          right: 0,
                          left: 0,
                          bottom: 100,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="descripcion"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          tick={xAxisTick}
                        />
                        <YAxis
                          tick={axisStyle}
                          label={axisYLabel("Número de operaciones")}
                        />
                        <Tooltip />
                        <Legend
                          layout="horizontal"
                          align="left"
                          verticalAlign="top"
                          wrapperStyle={legendStyle}
                        />
                        <Bar
                          dataKey="opsAno"
                          name={moment(PMVSearchYear).year()}
                          fill={barColors.blue}
                          activeBar={
                            <Rectangle
                              fill={barColors.blue}
                              stroke={barColors.gray}
                            />
                          }
                          label={getPMVBarLabel(
                            PMVStatsDashboardList,
                            "pmvAno",
                            "opsAno",
                          )}
                        />
                        <Bar
                          dataKey="opsAnoAnterior"
                          name={moment(PMVSearchYear)
                            .subtract(1, "year")
                            .year()}
                          fill={barColors.orange}
                          activeBar={
                            <Rectangle
                              fill={barColors.orange}
                              stroke={barColors.gray}
                            />
                          }
                          label={getPMVBarLabel(
                            PMVStatsDashboardList,
                            "pmvAnoAnterior",
                            "opsAnoAnterior",
                            barColors.orange,
                          )}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Personalizada sin gama */}
                {PMVSelectedTab === "Personalizada" && !PMVIncludeRange && (
                  <div
                    ref={chartContainerRef}
                    style={{
                      width: "100%",
                      height: "550px",
                    }}
                  >
                    <div style={titleStyle}>Evolución de ventas</div>
                    <ResponsiveContainer
                      width="100%"
                      height={500}
                      style={{
                        maxWidth: 1000,
                        margin: "0 auto",
                      }}
                    >
                      <BarChart
                        width={500}
                        height={300}
                        barCategoryGap={5}
                        barGap={0}
                        data={PMVStatsDashboardList}
                        margin={barChartMargin}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="ano"
                          tick={axisStyle}
                          label={axisXLabel("Año")}
                        />
                        <YAxis
                          tick={axisStyle}
                          label={axisYLabel("Número de operaciones")}
                        />
                        <Tooltip />
                        <Legend
                          layout="horizontal"
                          align="left"
                          verticalAlign="top"
                          wrapperStyle={legendStyle}
                        />
                        <Bar
                          dataKey="ops"
                          name={moment(PMVSearchYear).year()}
                          fill={barColors.blue}
                          activeBar={
                            <Rectangle
                              fill={barColors.blue}
                              stroke={barColors.gray}
                            />
                          }
                          label={getPMVBarLabel(
                            PMVStatsDashboardList,
                            "pmv",
                            "ops",
                          )}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Personaliza con gama */}
                {PMVSelectedTab === "Personalizada" && PMVIncludeRange && (
                  <div
                    ref={chartContainerRef}
                    style={{
                      width: "100%",
                      height: "550px",
                    }}
                  >
                    <div style={titleStyle}>
                      Evolución de ventas de gafas progresivas por gama
                    </div>
                    <ResponsiveContainer
                      width="100%"
                      height={500}
                      style={{
                        maxWidth: 1000,
                        margin: "0 auto",
                      }}
                    >
                      <BarChart
                        width={500}
                        height={300}
                        barCategoryGap={5}
                        barGap={0}
                        data={PMVStatsDashboardList}
                        margin={barChartMargin}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="ano"
                          label={axisXLabel("Año")}
                          tick={axisStyle}
                        />
                        <YAxis
                          tick={axisStyle}
                          label={axisYLabel("Número de operaciones")}
                        />
                        <Tooltip />
                        <Legend
                          layout="horizontal"
                          align="left"
                          verticalAlign="top"
                          wrapperStyle={legendStyle}
                        />
                        <Bar
                          dataKey="opsGamaBaja"
                          name="Gama baja"
                          fill={barColors.blue}
                          activeBar={
                            <Rectangle
                              fill={barColors.blue}
                              stroke={barColors.gray}
                            />
                          }
                          label={getPMVBarLabel(
                            PMVStatsDashboardList,
                            "pmvGamaBaja",
                            "opsGamaBaja",
                          )}
                        />
                        <Bar
                          dataKey="opsGamaMedia"
                          name="Gama media"
                          fill={barColors.orange}
                          activeBar={
                            <Rectangle
                              fill={barColors.orange}
                              stroke={barColors.gray}
                            />
                          }
                          label={getPMVBarLabel(
                            PMVStatsDashboardList,
                            "pmvGamaMedia",
                            "opsGamaMedia",
                            barColors.orange,
                          )}
                        />
                        <Bar
                          dataKey="opsGamaAlta"
                          name="Gama alta"
                          fill={barColors.red}
                          activeBar={
                            <Rectangle
                              fill={barColors.red}
                              stroke={barColors.gray}
                            />
                          }
                          label={getPMVBarLabel(
                            PMVStatsDashboardList,
                            "pmvGamaAlta",
                            "opsGamaAlta",
                            barColors.red,
                          )}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            {selectedTimeView === "Beneficios" && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 20 }}
              >
                <TabSelector
                  options={["5 Años", "10 Años"]}
                  selected={selectedYearView}
                  onChange={(e) => {
                    setSelectedYearView(e);
                  }}
                />
                <div style={{ width: "100%", height: "550px" }}>
                  <div style={titleStyle}>
                    Comparativa anual de beneficios bruto y neto
                  </div>
                  <ResponsiveContainer
                    width="100%"
                    height={500}
                    style={{
                      maxWidth: 1000,
                      margin: "0 auto",
                    }}
                  >
                    <BarChart
                      width={500}
                      height={300}
                      barCategoryGap={5}
                      barGap={0}
                      data={profitStatsDashboardList}
                      margin={barChartMargin}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="ano"
                        tick={axisStyle}
                        label={axisXLabel("Año")}
                      />
                      <YAxis
                        tick={axisStyle}
                        label={axisYLabel("Cantidad (€)")}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          const labels = {
                            beneficioBruto: "Beneficio bruto",
                            beneficioNeto: "Beneficio neto",
                          };
                          return [
                            formatValueUnit(value, "€"),
                            labels[name] || name,
                          ];
                        }}
                      />
                      <Legend
                        layout="horizontal"
                        align="left"
                        verticalAlign="top"
                        wrapperStyle={legendStyle}
                      />
                      <Bar
                        dataKey="beneficioBruto"
                        name="Beneficio bruto"
                        fill={barColors.blue}
                        activeBar={
                          <Rectangle
                            fill={barColors.blue}
                            stroke={barColors.gray}
                          />
                        }
                        label={getProfitBarLabel(
                          profitStatsDashboardList,
                          "beneficioBruto",
                          "porcBeneficioBruto",
                        )}
                      />
                      <Bar
                        dataKey="beneficioNeto"
                        name="Beneficio neto"
                        fill={barColors.orange}
                        activeBar={
                          <Rectangle
                            fill={barColors.orange}
                            stroke={barColors.gray}
                          />
                        }
                        label={getProfitBarLabel(
                          profitStatsDashboardList,
                          "beneficioNeto",
                          "porcBeneficioNeto",
                          barColors.orange,
                        )}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
